// janus-engine/inventory/destruction-payload.ts — R1b-2: the DESTRUCTION PAYLOAD half R1 owns (snapshot + enqueue + drain plan).
//
// PURE. An object carrying `destruction_effect_ability` resolves those abilities over the space it occupied when it is destroyed
// (the "exploding barrel"). Legacy splits it in two (includes/functions/janus/items/environmental_payload_dispatcher.php):
//   1. handle_inventory_destroyed_from_damage() SNAPSHOTS the job BEFORE anything mutates the row (the spill reparents children,
//      the pile replaces the footprint, delete_inventory() tombstones it) and ENQUEUES it (ft_enqueue_environmental_payload);
//   2. once the outermost destruction frame closes, ft_drain_environmental_payloads() walks the queue FIFO (a payload that
//      destroys another object appends to the same queue — chain reactions run iteratively, 256 resolutions max), and
//      ft_resolve_environmental_payload() orders each job's abilities by MEANING (surfaces before bursts, stable within each
//      group), drops a payload whose `requires_destruction_by_damage_type` does not match what broke the object, refuses a
//      non-AOE payload, anchors each one on the object's tile (janus_trigger_auto_place_aoe) and runs janus() in TRIGGER mode
//      (no AP / reaction debit) with the destroyer as the attributed caster.
// This module is everything in that path that is NOT an ability resolution: the snapshot, the per-job step plan (order, gates,
// the exact casting-target string janus() receives) and the FIFO drain driver. Running a step is Lane A's pipeline — the runner
// is INJECTED (`PayloadStepRunner`), so the port is a wiring change once the browser can run an environmental payload
// (cross-lane ask in plans/janus-r1b-destruction-residuals-handoff-2026-09-27.md §Cross-lane asks).

import { SHAPES as AOE_SHAPES } from '../ability/aoe/expand-placed-aoe-targets.js';

type Row = Record<string, unknown>;

export const DESTRUCTION_PAYLOAD_SCHEMA = 'janus.inventory.destruction-payload.v1' as const;
/** FT_ENVIRONMENTAL_PAYLOAD_MAX_RESOLUTIONS — the per-request backstop against a fabricate/destroy cycle. */
export const PAYLOAD_MAX_RESOLUTIONS = 256;

/** The readers a payload plan needs (a subset of ObjectDestroyReaders — the same page twins). */
export interface PayloadReaders {
  value: (row: Row, name: string) => unknown;
  entries: (row: Row, name: string) => unknown[];
  definition: (itemId: string) => Row | null;
}

/** Who the payload is attributed to (legacy `attributed_caster`): the GM tool's acting USER (no position), or the creature that broke
 * it. PHP also aims a directional (cone / line) payload AT that arr's position (janus_trigger_auto_place_aoe's inciting target), so a
 * creature carries its floor position when the caller knows it; without it a directional payload hands back (reviewer R1b-2 #1). */
export type PayloadCaster = { kind: 'user'; id: string } | { kind: 'creature'; id: string; position?: { x_pos: number; y_pos: number } | null };

/** ft_enqueue_environmental_payload()'s job — a pure snapshot taken BEFORE the destruction mutates anything. */
export interface DestructionPayloadJob {
  schema: typeof DESTRUCTION_PAYLOAD_SCHEMA;
  /** ft_normalize_layer_entries_to_distinct_uuids(layer_entries(row, 'destruction_effect_ability')) — distinct, served order. */
  ability_ids: string[];
  /** The destroyed row: dropped from the roster before its own payload expands targets (it is never caught by its own blast). */
  source_inventory_id: string;
  encounter_id: string;
  /** The destruction's roll group — the operation id (the truth commit writes its log rows under it). */
  roll_id: string;
  /** The object's own position at the moment of destruction — every payload is anchored here. */
  origin: { x_pos: number; y_pos: number; z_pos: number };
  attributed_caster: PayloadCaster;
  /** ft_damage_types_in_roll() of the breaking roll — [] for the GM tool (its stub roll carries no damage type). */
  destroyed_by_damage_types: string[];
}

/** One ability of a job, decided before any draw (ft_resolve_environmental_payload's loop body, minus janus()). */
export type PayloadStep =
  | { ability_id: string; action: 'resolve'; kind: 'surface' | 'burst'; aoe_shape: string;
      /** janus()'s `status_casting_targets_arr` — the FT3D six-coordinate span janus_trigger_auto_place_aoe() builds. */
      casting_targets: string;
      /** A burst that deals damage asks ft_try_ignite_surfaces_at() afterwards (surface chemistry) — the runner's to answer. */
      ignite: { x: number; z: number; radius_ft: number; damage_types: string[] } | null }
  | { ability_id: string; action: 'skip'; why: 'requires-destruction-by-damage-type' | 'not-aoe' };

export type PayloadPlan = { steps: PayloadStep[] } | { reason: string; definitionIds?: string[] };

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const uuidRe = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const isUuid = (value: unknown): boolean => uuidRe.test(upper(value));

/** layer_list_entry_uuid() twin: a card-list entry (structured row, bucketed / legacy shell, or string) → its item id ('' if none). */
export function layerEntryUuid(entry: unknown): string {
  const cut = (s: string): string => s.includes('|') ? s.split('|', 1)[0]! : s;
  const row = record(entry);
  if (row) {
    const effect = row.item_layer_effect;
    if (typeof effect === 'string' && effect !== '') return cut(effect);
    const bucketed = record(row.items)?.item_id;
    if (typeof bucketed === 'string' && bucketed !== '') return cut(bucketed);
    const own = row.item_id;
    if (typeof own === 'string' && own !== '') return cut(own);
    return '';
  }
  return typeof entry === 'string' ? cut(entry) : '';
}
/** ft_normalize_layer_entries_to_distinct_uuids() twin (upper-cased: every comparison downstream is case-insensitive). */
export function distinctEntryUuids(entries: unknown[]): string[] {
  const out: string[] = [];
  for (const entry of entries) {
    const id = upper(layerEntryUuid(entry));
    if (isUuid(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Definitions a payload's authored layers reference (statuses, skills, types and nested payloads). */
export function payloadDefinitionIds(definition: Row | null): string[] {
  const ids = new Set<string>();
  const walk = (value: unknown): void => {
    if (Array.isArray(value)) { value.forEach(walk); return; }
    if (record(value)) { Object.values(value as Row).forEach(walk); return; }
    if (typeof value === 'string') {
      const id = upper(value.split('|')[0]);
      if (isUuid(id)) ids.add(id);
    }
  };
  walk(record(definition?.item_layer_types));
  return [...ids];
}
/** PHP empty() on a layer_value() scalar. */
function phpEmpty(value: unknown): boolean {
  const v = scalar(value);
  return v === null || v === undefined || v === false || v === 0 || v === '' || v === '0' || (Array.isArray(v) && v.length === 0);
}
/** A layer_value() that may be a catalog shell → its id; a bare string as served. */
function layerScalarId(read: PayloadReaders, row: Row, name: string): string {
  const raw = scalar(read.value(row, name));
  if (typeof raw === 'string') return raw;
  const shell = record(raw);
  const id = record(shell?.items)?.item_id ?? shell?.item_id;
  return typeof id === 'string' ? id : '';
}

/**
 * PHP's (string) cast of a float (`precision` = 14, %G-style): what janus_trigger_auto_place_aoe() concatenates. Null when PHP
 * would switch to exponent notation (|n| < 1e-4 or ≥ 1e14) — the caller hands back rather than guess the spelling.
 */
export function phpFloatString(n: number): string | null {
  if (!Number.isFinite(n)) return null;
  if (n === 0) return Object.is(n, -0) ? '-0' : '0';
  const abs = Math.abs(n);
  if (abs < 1e-4 || abs >= 1e14) return null;
  const rounded = Number(n.toPrecision(14));
  return Object.is(rounded, -0) ? '-0' : String(rounded);
}

/**
 * janus_trigger_auto_place_aoe() twin for a destruction payload: anchored on the object; the inciting target is the attributed
 * caster arr. Centred shapes span origin → origin. A cone / line points from the object toward the inciting point — for the GM
 * tool's USER actor PHP reads no position, so the point defaults to the origin and the zero-length fallback points +x at full
 * distance; for a creature, `inciting` is its floor position (`undefined` = the caller passed none → null, hand back).
 */
export function payloadCastingTargets(encounterId: string, origin: { x_pos: number; y_pos: number; z_pos: number }, shape: string, aoeDistance: number,
  inciting: { x_pos: number; y_pos: number } | null | undefined): string | null {
  const ox = origin.x_pos, oy = origin.y_pos, oz = origin.z_pos;
  let dx: number, dy: number;
  if (['sphere', 'circle', 'cube', 'square', 'cylinder'].includes(shape)) { dx = ox; dy = oy; }
  else {
    if (inciting === undefined) return null;
    const vx = (inciting?.x_pos ?? ox) - ox, vy = (inciting?.y_pos ?? oy) - oy;
    const len = Math.sqrt(vx * vx + vy * vy);
    if (len < 0.01 || aoeDistance <= 0) { dx = ox + aoeDistance; dy = oy; } // PHP's zero-length fallback
    else { dx = ox + (vx / len) * aoeDistance; dy = oy + (vy / len) * aoeDistance; }
  }
  const parts = [ox, oy, oz, dx, dy, oz].map(phpFloatString);
  if (!isUuid(encounterId) || parts.some(p => p === null)) return null;
  return `${upper(encounterId)}|${parts.join(':')}`;
}

/**
 * The snapshot legacy takes before mutating (handle_inventory_destroyed_from_damage: `$destruction_payload_ability_ids`,
 * `$destruction_origin_arr = $target_arr`). Null when the row carries no payload.
 */
export function snapshotDestructionPayload(read: PayloadReaders, row: Row, at: {
  encounterId: string; sourceInventoryId: string; operationId: string; origin: { x_pos: number; y_pos: number; z_pos: number };
  caster: PayloadCaster; destroyedByDamageTypes?: string[];
}): DestructionPayloadJob | null {
  const abilityIds = distinctEntryUuids(read.entries(row, 'destruction_effect_ability'));
  if (abilityIds.length === 0) return null;
  return {
    schema: DESTRUCTION_PAYLOAD_SCHEMA,
    ability_ids: abilityIds,
    source_inventory_id: upper(at.sourceInventoryId),
    encounter_id: upper(at.encounterId),
    roll_id: upper(at.operationId),
    origin: { x_pos: at.origin.x_pos, y_pos: at.origin.y_pos, z_pos: at.origin.z_pos },
    attributed_caster: at.caster.kind === 'user' ? { kind: 'user', id: upper(at.caster.id) }
      : { kind: 'creature', id: upper(at.caster.id), ...(at.caster.position ? { position: { x_pos: at.caster.position.x_pos, y_pos: at.caster.position.y_pos } } : {}) },
    destroyed_by_damage_types: distinctEntryUuids(at.destroyedByDamageTypes ?? []),
  };
}

/**
 * ft_resolve_environmental_payload()'s loop, decided before any draw: every ability's definition (a missing one asks for a
 * fetch), SURFACES (persistent_aoe with no damage_type) before BURSTS (stable within each), the conditional gate, the AOE
 * refusal, and the exact casting string. PHP skips an unloadable ability with a warning; a page cannot tell "absent from this
 * catalog" from "absent", so it asks for the definitions instead (the caller fetches once, then hands back if still missing).
 */
export function planPayloadDrain(read: PayloadReaders, job: DestructionPayloadJob): PayloadPlan {
  const missing = job.ability_ids.filter(id => !read.definition(id));
  if (missing.length > 0) return { reason: 'payload-definition-missing', definitionIds: missing };
  const loaded = job.ability_ids.map(id => ({ id, def: read.definition(id)! }));
  const damageTypes = (def: Row): string[] => distinctEntryUuids(read.entries(def, 'damage_type'));
  const isSurface = (def: Row): boolean => !phpEmpty(read.value(def, 'persistent_aoe')) && damageTypes(def).length === 0;
  const ordered = [...loaded.filter(a => isSurface(a.def)), ...loaded.filter(a => !isSurface(a.def))];
  const steps: PayloadStep[] = [];
  for (const { id, def } of ordered) {
    const requires = distinctEntryUuids(read.entries(def, 'requires_destruction_by_damage_type'));
    if (requires.length > 0 && !requires.some(t => job.destroyed_by_damage_types.includes(t))) {
      steps.push({ ability_id: id, action: 'skip', why: 'requires-destruction-by-damage-type' });
      continue;
    }
    if (phpEmpty(read.value(def, 'aoe_type'))) { steps.push({ ability_id: id, action: 'skip', why: 'not-aoe' }); continue; }
    const typeId = layerScalarId(read, def, 'aoe_type');
    // AoeType::getVariableName(): an unknown id resolves to '' (a directional placement); a non-uuid value is its own name.
    const shape = isUuid(typeId) ? (AOE_SHAPES[upper(typeId)] ?? '') : typeId.toLowerCase();
    const distanceRaw = Number(scalar(read.value(def, 'aoe_distance')) ?? 0);
    const distance = Number.isFinite(distanceRaw) ? distanceRaw : 0;
    const caster = job.attributed_caster;
    // A user actor has no position (PHP: the origin); a creature's must be known for a directional payload.
    const inciting = caster.kind === 'user' ? null : (caster.position ?? undefined);
    const casting = payloadCastingTargets(job.encounter_id, job.origin, shape, distance, inciting);
    if (casting === null) return { reason: 'payload-anchor-unspellable' };
    const types = damageTypes(def);
    steps.push({ ability_id: id, action: 'resolve', kind: isSurface(def) ? 'surface' : 'burst', aoe_shape: shape, casting_targets: casting,
      ignite: types.length > 0 ? { x: job.origin.x_pos, z: job.origin.z_pos, radius_ft: distance, damage_types: types } : null });
  }
  return { steps };
}

/** What running one step produced: further jobs (an object the payload destroyed carried its own payload), or a hand-back. */
export type PayloadStepResult = { ok: true; enqueued?: DestructionPayloadJob[] } | { ok: false; handBack: string };
/** Lane A's entry point, injected: run ONE resolve step of a job (trigger mode, attributed caster, post-destruction roster). */
export type PayloadStepRunner = (job: DestructionPayloadJob, step: Extract<PayloadStep, { action: 'resolve' }>) => PayloadStepResult;

export interface PayloadDrainResult {
  /** Every step run, in order (job index into `jobs`, step). */
  ran: Array<{ job: number; step: PayloadStep }>;
  jobs: DestructionPayloadJob[];
  /** Set when a plan or a step handed back: the drain stopped there (nothing after it ran). */
  handBack: string | null;
  /** PHP's budget warning: the queue still held work after PAYLOAD_MAX_RESOLUTIONS resolutions and was dropped. */
  budgetExhausted: boolean;
}

/**
 * ft_drain_environmental_payloads() twin: FIFO over the queue, jobs a step enqueues append to the same walk, one resolution per
 * JOB counted against the budget (legacy counts jobs, not abilities). A plan or step that hands back stops the drain — a
 * half-drained chain cannot be finished by the legacy lane, so a caller must decide every hand-back BEFORE committing anything
 * (pre-draw; memory reference_client_truth_predraw_handback_fairness).
 */
export function drainPayloadQueue(initial: DestructionPayloadJob[], read: PayloadReaders, run: PayloadStepRunner): PayloadDrainResult {
  const jobs = [...initial];
  const ran: PayloadDrainResult['ran'] = [];
  let resolved = 0;
  for (let i = 0; i < jobs.length; i++) {
    if (resolved >= PAYLOAD_MAX_RESOLUTIONS) return { ran, jobs, handBack: null, budgetExhausted: true };
    resolved++;
    const plan = planPayloadDrain(read, jobs[i]!);
    if ('reason' in plan) return { ran, jobs, handBack: plan.reason, budgetExhausted: false };
    for (const step of plan.steps) {
      ran.push({ job: i, step });
      if (step.action !== 'resolve') continue;
      const result = run(jobs[i]!, step);
      if (!result.ok) return { ran, jobs, handBack: result.handBack, budgetExhausted: false };
      for (const next of result.enqueued ?? []) jobs.push(next);
    }
  }
  return { ran, jobs, handBack: null, budgetExhausted: false };
}
